// Combat panel — fixed-position UI that appears when the player has an
// active combat target. Shows target art + HP bar, player HP/MP, ability
// buttons (basic attack + 3 class abilities + flee), and a rolling log.
// Auto-mounts once; auto-shows/hides based on ClientState.combat.

import type { Net } from "../net.js";
import type { ClientStore, ClientState, CombatView } from "../state.js";
import { assetUrl } from "../asset_loader.js";

export function mountCombatPanel(_uiHost: HTMLElement, net: Net, store: ClientStore): void {
  const panel = document.createElement("div");
  panel.className = "combat-panel";
  panel.id = "combat-panel";
  panel.style.display = "none";
  document.body.appendChild(panel);

  let lastTargetId: number | null = null;
  const cooldownAt: Record<string, number> = {}; // client-side cooldown tracking (rounds remaining per ability id)

  function render(s: ClientState): void {
    if (!s.combat || s.phase !== "playing") {
      panel.style.display = "none";
      lastTargetId = null;
      return;
    }
    panel.style.display = "";
    const c = s.combat;
    // On target change, reset cooldown tracker
    if (c.target_id !== lastTargetId) {
      for (const k of Object.keys(cooldownAt)) delete cooldownAt[k];
      lastTargetId = c.target_id;
    }
    // Decrement cooldowns on each new log entry (approximation — every combat
    // round adds log entries, so we tick here).
    panel.innerHTML = `
      <header class="combat-header">
        <span class="combat-title">Engaged</span>
        <button id="combat-flee" class="combat-flee" title="Attempt to flee (60% success)">Flee</button>
      </header>
      <section class="combat-target">
        <img class="combat-target-art" alt="" src="${escape(assetUrl("mob", c.target_template_id, { name: c.target_name, category: "mob" }))}" />
        <div class="combat-target-info">
          <div class="combat-target-name">${escape(c.target_name)}</div>
          ${hpBar(c.target_hp, c.target_hp_max, "mob")}
        </div>
      </section>
      <section class="combat-player">
        ${hpBar(c.player_hp, c.player_hp_max, "hp")}
        ${s.stats ? hpBar(s.stats.mp, s.stats.mp_max, "mp") : ""}
      </section>
      <section class="combat-abilities">
        <button class="ab-btn" data-ab="basic_attack" title="Basic Attack (no cost)">
          <span class="ab-name">Attack</span>
          <span class="ab-meta">free</span>
        </button>
        ${c.abilities.slice(0, 4).map((a) => {
          const cd = cooldownAt[a.id] ?? 0;
          const disabled = cd > 0 || (s.stats ? s.stats.mp < a.cost : false);
          return `
            <button class="ab-btn ${cd > 0 ? "cooling" : ""}" data-ab="${escape(a.id)}" title="${escape(a.description)}" ${disabled ? "disabled" : ""}>
              <span class="ab-name">${escape(a.name)}</span>
              <span class="ab-meta">${a.cost > 0 ? a.cost + " mp" : "free"}${cd > 0 ? " · cd " + cd : ""}</span>
            </button>
          `;
        }).join("")}
      </section>
      <section class="combat-log">
        ${c.log.slice(-6).map((e) => `
          <div class="clog-line clog-${escape(e.kind)}">${escape(e.text)}</div>
        `).join("")}
      </section>
    `;

    for (const btn of panel.querySelectorAll<HTMLButtonElement>(".ab-btn")) {
      btn.addEventListener("click", () => {
        const id = btn.dataset.ab!;
        if (id === "basic_attack") {
          net.send({ t: "attack", mob_id_or_name: c.target_id });
        } else {
          const ability = c.abilities.find((a) => a.id === id);
          if (!ability) return;
          // Mark cooldown client-side so button disables immediately; server is authoritative
          cooldownAt[id] = ability.cooldown_rounds + 1;
          net.send({ t: "ability", ability_id: id, mob_id_or_name: c.target_id });
        }
        // Tick down cooldowns locally on each click as round-approximation
        for (const k of Object.keys(cooldownAt)) {
          cooldownAt[k] = Math.max(0, cooldownAt[k] - 1);
        }
      });
    }
    panel.querySelector<HTMLButtonElement>("#combat-flee")?.addEventListener("click", () => {
      net.send({ t: "flee" });
    });
  }

  function hpBar(cur: number, max: number, kind: "hp" | "mp" | "mob"): string {
    const pct = Math.max(0, Math.min(100, (cur / Math.max(1, max)) * 100));
    const label = kind === "hp" ? "HP" : kind === "mp" ? "MP" : "HP";
    return `
      <div class="combat-bar combat-bar-${kind}">
        <span class="combat-bar-label">${label}</span>
        <div class="combat-bar-track"><div class="combat-bar-fill" style="width:${pct.toFixed(1)}%"></div></div>
        <span class="combat-bar-value">${cur} / ${max}</span>
      </div>
    `;
  }

  store.subscribe(render);
  render(store.get());
}

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
